import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@academy/database-types';
import type { BootstrapData } from '@academy/machines';
import type { CommandEnvelope, CommandResult } from '@academy/domain';
import { beginNavigationRequest } from './navigation-telemetry';
import { installQuerySnapshotValidator, purgeAllQuerySnapshots } from './query-snapshot-storage';

let instance: SupabaseClient<Database> | null = null;

const navigationRequestCategory = (input: RequestInfo | URL) => {
  const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
  return url.includes('/auth/v1/') ||
    url.includes('account_bootstrap_ui_v1') ||
    url.includes('capability_invalidations_ui_v1')
    ? 'authorization'
    : 'data';
};

export const firstRelation = <T>(value: T | readonly T[] | null | undefined): T | null => {
  if (Array.isArray(value)) {
    const items = value as readonly T[];
    return items[0] ?? null;
  }
  return value === undefined ? null : (value as T | null);
};

export const environmentStatus = () => ({
  configured: Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY),
  missing: [
    !import.meta.env.VITE_SUPABASE_URL ? 'VITE_SUPABASE_URL' : null,
    !import.meta.env.VITE_SUPABASE_ANON_KEY ? 'VITE_SUPABASE_ANON_KEY' : null,
  ].filter((value): value is string => value !== null),
});

export const getSupabase = () => {
  if (instance) return instance;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !key) throw new Error('Supabase browser configuration is missing.');
  instance = createClient<Database>(url, key, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    realtime: { params: { eventsPerSecond: 10 } },
    global: {
      fetch: async (input, init) => {
        const request = beginNavigationRequest(navigationRequestCategory(input));
        try {
          const response = await globalThis.fetch(input, init);
          request.complete(response);
          return response;
        } catch (error) {
          request.complete();
          throw error;
        }
      },
    },
  });
  return instance;
};

export const uploadToSignedProviderUrl = async (signedUrl: string, body: Blob) => {
  const url = new URL(signedUrl);
  const marker = '/object/upload/sign/';
  const signedPath = url.pathname.slice(url.pathname.indexOf(marker) + marker.length);
  const [bucketId, ...encodedSegments] = signedPath.split('/');
  const token = url.searchParams.get('token');
  if (!bucketId || encodedSegments.length === 0 || !token) {
    throw new Error('The private provider upload grant is invalid.');
  }
  const providerPath = encodedSegments.map((segment) => decodeURIComponent(segment)).join('/');
  const result = await getSupabase()
    .storage.from(bucketId)
    .uploadToSignedUrl(providerPath, token, body, {
      contentType: body.type || 'application/octet-stream',
    });
  if (result.error) throw result.error;
};

const sha256Hex = async (value: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const isAuthoritativeSessionError = (error: unknown) => {
  if (typeof error !== 'object' || error === null) return false;
  const status: unknown = Reflect.get(error, 'status') ?? Reflect.get(error, 'statusCode');
  const code: unknown = Reflect.get(error, 'code');
  return status === 401 || status === 403 || code === 'JWT_EXPIRED' || code === 'BAD_JWT';
};

export const loadBootstrap = async (_signal: AbortSignal): Promise<BootstrapData | null> => {
  if (!environmentStatus().configured) return null;
  const client = getSupabase();
  const { data, error: sessionError } = await client.auth.getSession();
  if (sessionError) {
    if (isAuthoritativeSessionError(sessionError)) purgeAllQuerySnapshots();
    throw sessionError;
  }
  if (!data.session) {
    purgeAllQuerySnapshots();
    return null;
  }
  const { data: account, error } = await client
    .from('account_bootstrap_ui_v1' as 'user_accounts_ui_v1')
    .select('account_id,email,active_roles')
    .abortSignal(_signal)
    .single();
  if (error) {
    if (isAuthoritativeSessionError(error)) purgeAllQuerySnapshots();
    throw error;
  }
  const roles = (account.active_roles ?? []).filter(
    (code): code is BootstrapData['roles'][number] =>
      typeof code === 'string' &&
      [
        'ACADEMY_ADMIN',
        'ACADEMIC_COORDINATOR',
        'TEACHER',
        'STUDENT',
        'GUARDIAN',
        'SUPPORT',
        'SAFETY_OFFICER',
        'PRIVACY_OFFICER',
        'AUDITOR',
      ].includes(code),
  );
  const userId = data.session.user.id;
  const sessionGeneration =
    data.session.user.last_sign_in_at ?? data.session.user.created_at ?? 'initial-session';
  const [sessionSubjectHash, sessionGenerationHash, snapshotValidation] = await Promise.all([
    sha256Hex(userId),
    sha256Hex(`${userId}:${sessionGeneration}`),
    import('./query-snapshot-validator'),
  ]);
  installQuerySnapshotValidator(snapshotValidation.querySnapshotValidator);
  return {
    accountId: Number(account.account_id),
    ...(account.email ? { email: account.email } : {}),
    displayName:
      typeof data.session.user.user_metadata.full_name === 'string'
        ? data.session.user.user_metadata.full_name
        : (account.email?.split('@')[0] ?? 'Academy member'),
    roles,
    sessionSubjectHash,
    sessionGenerationHash,
  };
};

export const refreshBootstrap = async (signal: AbortSignal): Promise<BootstrapData | null> => {
  return loadBootstrap(signal);
};

export const sendCommand = async <TData>(
  envelope: CommandEnvelope,
): Promise<CommandResult<TData>> => {
  const client = getSupabase();
  const response = await client.functions.invoke<CommandResult<TData>>('domain-command', {
    body: envelope,
  });
  if (response.error && 'context' in response.error) {
    const context: unknown = Reflect.get(response.error, 'context');
    if (context instanceof Response) {
      const body: unknown = await context
        .clone()
        .json()
        .catch(() => null);
      if (
        typeof body === 'object' &&
        body !== null &&
        Reflect.get(body, 'ok') === false &&
        typeof Reflect.get(body, 'code') === 'string' &&
        typeof Reflect.get(body, 'message') === 'string' &&
        typeof Reflect.get(body, 'retryable') === 'boolean' &&
        typeof Reflect.get(body, 'correlationId') === 'string'
      ) {
        return body as CommandResult<TData>;
      }
    }
  }
  if (response.error || !response.data) {
    return {
      ok: false,
      code: 'INTERNAL_ERROR',
      message: 'The command endpoint could not be reached.',
      retryable: true,
      correlationId: envelope.correlationId,
    };
  }
  return response.data;
};

export const commandEnvelope = <T extends string, P>(
  command: T,
  payload: P,
  expectedVersion?: number,
): CommandEnvelope<T, P> => ({
  commandId: crypto.randomUUID(),
  idempotencyKey: crypto.randomUUID(),
  correlationId: crypto.randomUUID(),
  ...(expectedVersion === undefined ? {} : { expectedVersion }),
  command,
  payload,
});
